import { alpha, createTheme, darken, lighten } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { ButtonProps } from "@mui/material/Button";

declare module "@mui/material/styles" {
  interface Palette {
    surface: string;
    surfaceTint: string;
    outline: string;
    outlineVariant: string;
    surfaceVariant: string;
    surfaceDim: string;
    surfaceBright: string;
    surfaceContainerLowest: string;
    surfaceContainerLow: string;
    surfaceContainer: string;
    surfaceContainerHigh: string;
    surfaceContainerHighest: string;
  }

  interface PaletteOptions {
    surface?: string;
    surfaceTint?: string;
    outline?: string;
    outlineVariant?: string;
    surfaceVariant?: string;
    surfaceDim?: string;
    surfaceBright?: string;
    surfaceContainerLowest?: string;
    surfaceContainerLow?: string;
    surfaceContainer?: string;
    surfaceContainerHigh?: string;
    surfaceContainerHighest?: string;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    elevated: true;
    filledTonal: true;
  }
}

declare module "@mui/material/Dialog" {
  interface DialogPropsVariantOverrides {
    modal: true;
  }
}

declare module "@mui/material/DialogTitle" {
  interface DialogTitlePropsVariantOverrides {
    modal: true;
  }
}

declare module "@mui/material/DialogContent" {
  interface DialogContentPropsVariantOverrides {
    modal: true;
  }
}

declare module "@mui/material/DialogActions" {
  interface DialogActionsPropsVariantOverrides {
    modal: true;
  }
}

type ButtonStyleParams = {
  ownerState: ButtonProps;
  theme: Theme;
};

type ThemeStyleParams = {
  theme: Theme;
};

type PaletteEntryColors = {
  main?: string;
  dark?: string;
  light?: string;
  contrastText?: string;
};

const resolveButtonPalette = (ownerState: ButtonProps, theme: Theme) => {
  const colorKey =
    ownerState.color && ownerState.color !== "inherit"
      ? ownerState.color
      : "primary";

  const paletteEntry =
    theme.palette[colorKey as keyof Theme["palette"]] as PaletteEntryColors | undefined;

  const main = paletteEntry?.main ?? theme.palette.primary.main;
  const dark = paletteEntry?.dark ?? darken(main, 0.15);
  const light = paletteEntry?.light ?? lighten(main, 0.5);
  const contrast = paletteEntry?.contrastText ?? theme.palette.getContrastText(main);

  return { main, dark, light, contrast };
};

const baseTheme = createTheme({
  palette: {
    primary: {
      main: "#00696a",
      light: "#d5f4e6", //dbffef
      dark: "#004f50",
      contrastText: "#ffffff",
    },

    secondary: {
      main: "#5d7494ff", //4c607c
      light: "#e4e8ef",
      dark: "#32394bff",
      contrastText: "#ffffff",
    },

    info: {
      main: "#1976d2",
      dark: "#344863",
      contrastText: "#ffffff",
    },

    error: {
      main: "#ba1a1a",
      dark: "#93000a",
      contrastText: "#ffffff",
    },

    background: {
      default: "#f4fbfa", // surface
      paper: "#fbfbfb",
    },

    text: {
      primary: "#161d1d",
      secondary: "#3b4247ff",
    },

    divider: "#bec8c8",

    grey: {
      50: "#ffffff",
      100: "#f2f6f9ff",
      200: "#eff2f5ff",
      300: "#e9ebefff",
      400: "#e3e6e9ff",
      500: "#dde2e4ff",
      600: "#d5d9dbff",
      700: "#bec5c8ff",
      800: "#6f7979",
      900: "#161d1d",
    },

    surfaceTint: "#00696a",

    outline: "#bec8c8",
    outlineVariant: "#d9e0e0",

    surface: 'f4fbfa',
    surfaceVariant: "#dae5e4",
    surfaceDim: "#d5dbdb",
    surfaceBright: "#f2f9f8",
    surfaceContainerLowest: "#ffffff",
    surfaceContainerLow: "#eff5f4",
    surfaceContainer: "#e9efee",
    surfaceContainerHigh: "#e3e9e9",
    surfaceContainerHighest: "#dde4e3",
  },
});

export const defaultTheme = createTheme(baseTheme, {
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica Neue', 'Arial', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    h1: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.625rem',
      fontWeight: 500,
      color: baseTheme.palette.primary.dark,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.375rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
    }
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }: ButtonStyleParams) => {
          const size = ownerState.size ?? "medium";
          const paddings: Record<string, string> = {
            small: "0 1rem",
            medium: "0 1.5rem",
            large: "0 1.5rem",
          };
          const heights: Record<string, number> = {
            small: 32,
            medium: 42,
            large: 48,
          };

          return {
            borderRadius: 9999,
            padding: paddings[size],
            minHeight: heights[size],
            textTransform: "none",
            "&.Mui-disabled": {
              color: alpha(theme.palette.text.primary, 0.38),
              borderColor: alpha(theme.palette.text.primary, 0.12),
            },
          };
        },
        contained: ({ ownerState, theme }: ButtonStyleParams) => {
          const { main, dark, contrast } = resolveButtonPalette(ownerState, theme);

          return {
            backgroundColor: main,
            color: contrast,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: dark,
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
          };
        },
        outlined: ({ ownerState, theme }: ButtonStyleParams) => {
          const { main, dark } = resolveButtonPalette(ownerState, theme);

          return {
            borderWidth: 1,
            borderColor: main,
            color: main,
            "&:hover": {
              borderColor: dark,
              backgroundColor: alpha(main, 0.08),
            },
            "&:active": {
              borderColor: dark,
              backgroundColor: alpha(main, 0.12),
            },
            "&.Mui-disabled": {
              borderColor: alpha(theme.palette.text.primary, 0.12),
              color: alpha(theme.palette.text.primary, 0.38),
            },
          };
        },
        text: ({ ownerState, theme }: ButtonStyleParams) => {
          const { main } = resolveButtonPalette(ownerState, theme);

          return {
            color: main,
            "&:hover": {
              backgroundColor: alpha(main, 0.08),
            },
            "&:active": {
              backgroundColor: alpha(main, 0.12),
            },
          };
        },
      },
      variants: [
        {
          props: { variant: "elevated" },
          style: ({ ownerState, theme }: ButtonStyleParams) => {
            const { main } = resolveButtonPalette(ownerState, theme);

            return {
              backgroundColor: alpha(main, 0.05),
              color: main,
              boxShadow: theme.shadows[1],
              "&:hover": {
                backgroundColor: alpha(main, 0.08),
                boxShadow: theme.shadows[2],
              },
              "&:active": {
                backgroundColor: alpha(main, 0.12),
                boxShadow: theme.shadows[1],
              },
              "&.Mui-disabled": {
                boxShadow: "none",
              },
            };
          },
        },
        {
          props: { variant: "filledTonal" },
          style: ({ ownerState, theme }: ButtonStyleParams) => {
            const { dark, light } = resolveButtonPalette(ownerState, theme);
            const hoverBackground = darken(light, 0.05);
            const activeBackground = darken(light, 0.1);

            return {
              backgroundColor: light,
              color: dark,
              "&:hover": {
                backgroundColor: hoverBackground,
                color: dark,
              },
              "&:active": {
                backgroundColor: activeBackground,
                color: dark,
              },
            };
          },
        },
      ],
    },

    MuiDialog: {
      variants: [
        {
          props: { variant: "modal" },
          style: ({ theme }: ThemeStyleParams) => ({
            "& .MuiDialog-paperScrollPaper": {
              borderRadius: 8,
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.16)}`,
              width: "100%",
              margin: theme.spacing(1.5),
              [theme.breakpoints.up("sm")]: {
                margin: theme.spacing(2),
              },
              [theme.breakpoints.up("md")]: {
                margin: theme.spacing(3),
              },
            },
          }),
        },
      ],
    },

    MuiDialogTitle: {
      variants: [
        {
          props: { variant: "modal" },
          style: ({ theme }: ThemeStyleParams) => ({
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1.5),
            padding: theme.spacing(2, 3),
            fontSize: theme.typography.h5.fontSize,
            fontWeight: '600',
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${alpha(theme.palette.outlineVariant, 0.6)}`,
            
            "& svg": {
              flexShrink: 0,
              fontSize: "1.75rem",
              color: theme.palette.primary.main,
              backgroundColor: `${alpha(theme.palette.primary.light, 0.5)}`,
              borderRadius: theme.shape.borderRadius,
              padding: theme.spacing(1.75),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }),
        },
      ],
    },

    MuiDialogContent: {
      variants: [
        {
          props: { variant: "modal" },
          style: ({ theme }: ThemeStyleParams) => ({
            padding: `${theme.spacing(2.5, 3, 2.5, 3)} !important`,
            color: theme.palette.text.secondary,
          }),
        },
      ],
    },

    MuiDialogActions: {
      variants: [
        {
          props: { variant: "modal" },
          style: ({ theme }: ThemeStyleParams) => ({
            padding: theme.spacing(1,3,2,3),
            display: "flex",
            justifyContent: "flex-end",
          }),
        },
      ],
    },

    MuiTableContainer: {
      styleOverrides: {
        root: () => ({
          borderRadius: 8,
          boxShadow: 'none',
          border: `1px solid ${baseTheme.palette.grey[300]}`,
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: () => ({
          backgroundColor: baseTheme.palette.grey[200],
          '& .MuiTableCell-root': {
            fontWeight: 600,
            color: '#374151',
            borderBottom: '1px solid #e5e7eb',
            padding: '12px 16px',
          },
        }),
      },
    },
    // MuiTableBody: {
    //   styleOverrides: {
    //     root: ({ theme }) => ({
    //       '& .MuiTableRow-root:hover': {
    //         backgroundColor: '#f3f4f6',
    //       },
    //       '& .MuiTableRow-root:nth-of-type(odd)': {
    //         backgroundColor: '#ffffff',
    //       },
    //       '& .MuiTableRow-root:nth-of-type(even)': {
    //         backgroundColor: '#f9fafb',
    //       },
    //     }),
    //   },
    // },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },

    MuiChip: {
      variants: [
        // 
        {
          props: { variant: 'filled', color: 'default' },
          style: () => ({
            backgroundColor: baseTheme.palette.secondary.light,
            color: baseTheme.palette.secondary.main,
            fontWeight: 600,
          }),
        },
        // success outlined
        {
          props: { variant: 'outlined', color: 'success' },
          style: () => ({
            backgroundColor: 'hsl(120, 80%, 98%)',
          }),
        },
        // error outlined
        {
          props: { variant: 'outlined', color: 'error' },
          style: () => ({
            backgroundColor: 'hsl(0deg 100% 96.08%)',
          }),
        },
        // warning outlined
        {
          props: { variant: 'outlined', color: 'warning' },
          style: () => ({
            backgroundColor: 'hsl(20.18deg 100% 98.52%)',
          }),
        },
        // info outlined
        {
          props: { variant: 'outlined', color: 'info' },
          style: () => ({
            backgroundColor: 'hsl(220.52deg 100% 97.45%)',
          }),
        }
      ],
    },
  },
});
